import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import React, { useEffect, useState, useRef } from 'react';
import UbigeoAutocomplete from '../components/UbigeoAutocomplete';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel, InputAdornment, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import { GridOn } from '@mui/icons-material';
import axios from 'axios';
import { exportGenericWorkbook } from '../utils/excel/genericWorkbook';

const initialForm = {
  nombre: '',
  direccion: '',
  ubigeo: '',
  telefono: '',
  email: '',
  es_principal: false,
  codigo_establecimiento: '0000'
};

export default function Sucursales() {
  const [sucursales, setSucursales] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [ubigeoSeleccionado, setUbigeoSeleccionado] = useState(null);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [submitError, setSubmitError] = useState('');
  const tableRef = useRef();

  const fetchSucursales = async () => {
    const { data } = await axios.get('/api/sucursales');
    setSucursales(data);
    setFiltered(data);
  };

  useEffect(() => { fetchSucursales(); }, []);

  useEffect(() => {
    let data = sucursales;
    if (search) {
      data = sucursales.filter(s =>
        s.nombre.toLowerCase().includes(search.toLowerCase()) ||
        s.direccion.toLowerCase().includes(search.toLowerCase()) ||
        (s.ubigeo || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.telefono || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.email || '').toLowerCase().includes(search.toLowerCase())
      );
    }
    setFiltered(data);
    setPage(0);
  }, [search, sucursales]);

  const handleOpen = async (sucursal = null) => {
    if (sucursal) {
      setForm({
        nombre: sucursal.nombre,
        direccion: sucursal.direccion,
        ubigeo: sucursal.ubigeo,
        telefono: sucursal.telefono,
        email: sucursal.email,
        es_principal: sucursal.es_principal === 1 || sucursal.es_principal === true,
        codigo_establecimiento: sucursal.codigo_establecimiento || '0000'
      });
      setEditId(sucursal.id);
      if (sucursal.ubigeo) {
        // Buscar el objeto ubigeo completo para el Autocomplete
        try {
          const { obtenerUbigeoPorCodigo } = await import('../utils/ubigeo');
          const obj = await obtenerUbigeoPorCodigo(sucursal.ubigeo);
          setUbigeoSeleccionado(obj || { codigo: sucursal.ubigeo });
        } catch {
          setUbigeoSeleccionado({ codigo: sucursal.ubigeo });
        }
      } else {
        setUbigeoSeleccionado(null);
      }
    } else {
      setForm(initialForm);
      setEditId(null);
      setUbigeoSeleccionado(null);
    }
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError('');
    try {
      if (editId) {
        await axios.put(`/api/sucursales/${editId}`, form);
      } else {
        await axios.post('/api/sucursales', form);
      }
      setOpen(false);
      fetchSucursales();
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Error al guardar sucursal');
    }
  };

  const handleDelete = async id => {
    if (window.confirm('¿Eliminar sucursal?')) {
      try {
        await axios.delete(`/api/sucursales/${id}`);
        fetchSucursales();
      } catch { alert('Error al eliminar sucursal'); }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800, mt: { xs: '0em', sm: '0em', md: '0em' } }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Sucursales</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOn />}
            onClick={async () => {
              const rows = filtered.map(s => ({
                id: s.id,
                nombre: s.nombre,
                direccion: s.direccion,
                ubigeo: s.ubigeo || '',
                telefono: s.telefono || '',
                email: s.email || '',
                codigo_establecimiento: s.codigo_establecimiento || '0000',
                principal: s.es_principal ? 'SÍ' : 'NO'
              }));
              await exportGenericWorkbook({
                titulo: 'REPORTE - SUCURSALES',
                sheetName: 'Sucursales',
                tableName: 'tblSucursales',
                fileName: 'Sucursales',
                columns: [
                  { key: 'id', label: 'ID', width: 8 },
                  { key: 'nombre', label: 'Nombre', width: 26 },
                  { key: 'direccion', label: 'Dirección', width: 30 },
                  { key: 'ubigeo', label: 'Ubigeo', width: 16 },
                  { key: 'telefono', label: 'Teléfono', width: 18 },
                  { key: 'email', label: 'Email', width: 28 },
                  { key: 'codigo_establecimiento', label: 'Cód. Estab.', width: 14 },
                  { key: 'principal', label: 'Principal', width: 14, isEstado: true }
                ],
                rows,
                searchText: search,
                usuario: ''
              });
            }}
          >Exportar Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>Nueva Sucursal</Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table ref={tableRef} sx={{ minWidth: { xs: 350, sm: 900, md: 1100 } }}>
            <TableHead>
              <TableRow>
                <TableCell align="center">Nombre</TableCell>
                <TableCell align="center">Dirección</TableCell>
                <TableCell align="center">Ubigeo</TableCell>
                <TableCell align="center">Teléfono</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Cód. Estab.</TableCell>
                <TableCell align="center">Principal</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length > 0 ? (
                filtered.slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage).map(s => (
                  <TableRow key={s.id}>
                    <TableCell align="center">{s.nombre}</TableCell>
                    <TableCell align="center">{s.direccion}</TableCell>
                    <TableCell align="center">{s.ubigeo}</TableCell>
                    <TableCell align="center">{s.telefono}</TableCell>
                    <TableCell align="center">{s.email}</TableCell>
                    <TableCell align="center">{s.codigo_establecimiento || '0000'}</TableCell>
                    <TableCell align="center">{s.es_principal ? 'Sí' : 'No'}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpen(s)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(s.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">Sin registros</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Paginación fuera del recuadro, alineada a la derecha, texto plano */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1, userSelect: 'none', fontSize: '0.9375rem', fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontWeight: 500 }}>
          <span
            style={{
              color: page === 0 ? '#aaa' : '#1976d2',
              cursor: page === 0 ? 'default' : 'pointer',
              marginRight: 16,
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 500
            }}
            onClick={() => page > 0 && setPage(page - 1)}
          >ANTERIOR</span>
          <span
            style={{
              color: (rowsPerPage === -1 ? true : (page + 1) * rowsPerPage >= filtered.length) ? '#aaa' : '#1976d2',
              cursor: (rowsPerPage === -1 ? true : (page + 1) * rowsPerPage >= filtered.length) ? 'default' : 'pointer',
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 500
            }}
            onClick={() => (rowsPerPage === -1 ? false : (page + 1) * rowsPerPage < filtered.length) && setPage(page + 1)}
          >SIGUIENTE</span>
        </Box>
        <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              {editId ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {submitError && <Typography color="error" sx={{ mb: 1 }}>{submitError}</Typography>}
              <TextField margin="dense" label="Nombre" name="nombre" value={form.nombre} onChange={handleChange} fullWidth required />
              <TextField margin="dense" label="Dirección" name="direccion" value={form.direccion} onChange={handleChange} fullWidth />
              <Box sx={{ mt: 1, mb: 0.5 }}>
                <UbigeoAutocomplete value={ubigeoSeleccionado} onChange={val => {
                  setUbigeoSeleccionado(val);
                  setForm(prev => ({ ...prev, ubigeo: val ? val.codigo : '' }));
                }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextField
                  margin="dense"
                  label="Teléfono"
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  sx={{ flex: 3 }}
                />
                <TextField
                  margin="dense"
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  sx={{ flex: 7 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                <FormControlLabel control={<Checkbox checked={form.es_principal} onChange={handleChange} name="es_principal" />} label="Principal" />
                <TextField
                  margin="dense"
                  label="Cód. Establecimiento SUNAT"
                  name="codigo_establecimiento"
                  value={form.codigo_establecimiento}
                  onChange={handleChange}
                  inputProps={{ maxLength: 4 }}
                  helperText="Ej: 0000 principal, 0001 anexo"
                  sx={{ width: '18em' }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} color="primary">Guardar</Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Box>
  );
}
